/**
 * Where a post lives on its platform.
 *
 * `Post.uri` is only trustworthy for some platforms: the X fetcher writes a
 * bare "0" or "1" into it, and Instagram and TikTok ad posts have no public
 * page at all, so their uri is empty. Anything that is not an http(s) URL is
 * treated as absent and, where the platform has a stable permalink scheme,
 * rebuilt from the service post id instead.
 */
export type PermalinkSource = {
    uri: string
    servicePostId: string
    socialMediaServiceShortName: string
    serviceAccountUsername?: string | null
}

const HTTP_URL = /^https?:\/\//i

export function postPermalink(post: PermalinkSource): string | undefined {
    if (HTTP_URL.test(post.uri.trim())) {
        return post.uri.trim()
    }
    switch (post.socialMediaServiceShortName.toLowerCase()) {
        case 'twitter': {
            // X redirects /<anything>/status/<id> to the right handle, and the
            // /i/status/ form works with no handle at all, so a missing
            // username still yields a working link.
            const handle = post.serviceAccountUsername?.replace(/^@/, '').trim()
            return handle
                ? `https://x.com/${handle}/status/${post.servicePostId}`
                : `https://x.com/i/status/${post.servicePostId}`
        }
        case 'youtube':
            return `https://www.youtube.com/watch?v=${post.servicePostId}`
        default:
            return undefined
    }
}

/**
 * Platforms whose post collection is not running, so the comment counts on
 * the Posts page cannot be trusted for them. X post collection stopped with
 * the snscrape scraper in June 2023 and the v2 mention fetch leaves comments
 * with no postId, so every X row here is a 2023 leftover with nothing newer
 * attached (checked against dc2, 21 Sep 2026).
 */
export function postCollectionNote(socialMediaServiceShortName: string): string | undefined {
    switch (socialMediaServiceShortName.toLowerCase()) {
        case 'twitter':
            return 'Post collection for X has been paused since June 2023, and X comments are not linked to posts. Counts here are not current.'
        default:
            return undefined
    }
}
